import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Row, Col, Card, Typography, Breadcrumb, Spin, Empty, Tag } from 'antd';
import { HomeOutlined, TagOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Paragraph } = Typography;

// Product interface
interface Product {
    _id: string;
    name: string;
    price: number;
    tire_size?: string;
    downloaded_images?: string[];
    brand_name?: string;
}

// Marka bilgileri ve SEO içerikleri
const brandData = {
    'lassa': {
        name: 'Lassa',
        slug: 'lassa-lastik',
        title: 'Lassa Lastik Modelleri ve Fiyatları | Sakarya Lastik',
        description: 'Lassa lastik modelleri ve en uygun fiyatları Sakarya\'da. Yaz, kış ve 4 mevsim Lassa lastikleri. ✓ Ücretsiz Montaj ✓ Garanti',
        content: `Lassa, Türkiye'nin önde gelen lastik markalarından biridir. Brisa Bridgestone Sabancı Lastik Sanayi ve Ticaret A.Ş. tarafından üretilen Lassa lastikler, yüksek kalite standartları ve uygun fiyat politikası ile öne çıkar.

**Lassa Lastik Özellikleri:**
- Uzun ömürlü ve dayanıklı yapı
- Yüksek çekiş gücü ve yol tutuş
- Düşük yuvarlanma direnci ile yakıt tasarrufu
- Sessiz ve konforlu sürüş deneyimi

**Lassa Lastik Modelleri:**
- **Yaz Lastikleri:** Competus H/L, Driveways, Greenways
- **Kış Lastikleri:** Snoways, Competus Winter
- **4 Mevsim:** Multiways

Sakarya Lastik olarak, tüm Lassa lastik modellerini stoklarımızda bulunduruyoruz. Ücretsiz montaj ve garanti ile hizmetinizdeyiz.`,
        keywords: ['lassa lastik', 'lassa lastik fiyatları', 'lassa yaz lastiği', 'lassa kış lastiği', 'lassa 4 mevsim lastik']
    },
    'michelin': {
        name: 'Michelin',
        slug: 'michelin-lastik',
        title: 'Michelin Lastik Modelleri ve Fiyatları | Premium Lastik',
        description: 'Michelin premium lastik modelleri Sakarya\'da. Pilot Sport, Primacy, CrossClimate serileri. ✓ Orijinal Ürün ✓ Uzman Montaj',
        content: `Michelin, dünya çapında tanınan premium lastik markasıdır. 1889'dan beri otomotiv sektörüne hizmet veren Michelin, yenilikçi teknolojileri ve üstün performansı ile bilinir.

**Michelin Teknolojileri:**
- Silica+ teknolojisi ile mükemmel yol tutuş
- Green X çevre dostu üretim
- MaxTouch Contact optimizasyonu
- Acoustic Technology sessiz sürüş

**Michelin Seriler:**
- **Pilot Sport:** Yüksek performans
- **Primacy:** Konfor ve güvenlik
- **CrossClimate:** 4 mevsim çözüm
- **Latitude:** SUV lastikleri

Sakarya'da Michelin yetkili bayisi olarak, orijinal Michelin lastiklerini en uygun fiyatlarla sunuyoruz.`,
        keywords: ['michelin lastik', 'michelin pilot sport', 'michelin primacy', 'michelin crossclimate', 'michelin fiyatları']
    },
    'continental': {
        name: 'Continental',
        slug: 'continental-lastik',
        title: 'Continental Lastik Modelleri | Alman Kalitesi',
        description: 'Continental lastik çeşitleri Sakarya\'da. PremiumContact, WinterContact, AllSeasonContact. ✓ Almanya Menşei ✓ Uzun Ömür',
        content: `Continental, 1871'den beri Alman mühendislik kalitesini lastik teknolojisine taşıyan lider markadır. Güvenlik, konfor ve uzun ömür Continental'in temel prensipleridir.

**Continental Avantajları:**
- SSR (Self Supporting Runflat) teknolojisi
- ContiSilent gürültü azaltma
- EcoContact çevre dostu serisi
- Alman kalite güvencesi

**Popüler Modeller:**
- PremiumContact 6 - Yaz lastiği
- WinterContact TS 870 - Kış lastiği
- AllSeasonContact - 4 mevsim
- SportContact - Performans

Continental lastiklerde Sakarya'nın en geniş stoğu bizde. Profesyonel montaj hizmeti ile güvenle kullanın.`,
        keywords: ['continental lastik', 'continental premiumcontact', 'continental wintercontact', 'continental fiyatları']
    },
    'bridgestone': {
        name: 'Bridgestone',
        slug: 'bridgestone-lastik',
        title: 'Bridgestone Lastik Çeşitleri | Japon Teknolojisi',
        description: 'Bridgestone lastik modelleri Sakarya\'da. Turanza, Potenza, Blizzak, Dueler. ✓ Japon Kalitesi ✓ Uzman Montaj',
        content: `Bridgestone, dünyanın en büyük lastik üreticisidir. 1931'den beri Japon hassasiyeti ve teknolojisi ile üstün lastikler üretmektedir.

**Bridgestone İnovasyon:**
- NanoPro-Tech teknolojisi
- Enliten hafif yapı
- DriveGuard run-flat sistem
- Ologic aerodinamik tasarım

**Bridgestone Serileri:**
- **Turanza:** Konfor ve sessizlik
- **Potenza:** Sport performans
- **Blizzak:** Kış güvenliği
- **Dueler:** SUV ve arazi

Sakarya Lastik'te tüm Bridgestone modelleri stoklarımızda. Ücretsiz balans ve garanti dahil.`,
        keywords: ['bridgestone lastik', 'bridgestone turanza', 'bridgestone potenza', 'bridgestone blizzak']
    },
    'pirelli': {
        name: 'Pirelli',
        slug: 'pirelli-lastik',
        title: 'Pirelli Lastik Modelleri | İtalyan Performans',
        description: 'Pirelli performans lastikleri Sakarya\'da. Cinturato, Scorpion, P Zero, Winter Sottozero. ✓ İtalya Kalitesi ✓ Spor Lastik',
        content: `Pirelli, 1872'den beri İtalyan tutkusu ve mühendislik mükemmelliğini bir araya getiren prestijli lastik markasıdır. Formula 1'in resmi lastik tedarikçisidir.

**Pirelli Performans:**
- Run Flat teknolojisi
- Seal Inside iç tamir sistemi
- Eco & Safety çevre ve güvenlik
- Motorsport geçmişi

**Pirelli Koleksiyonu:**
- **Cinturato:** Günlük kullanım
- **Scorpion:** SUV serisi
- **P Zero:** Ultra performans
- **Winter Sottozero:** Kış sporları

Pirelli'nin gücünü Sakarya'da deneyimleyin. Profesyonel ekibimiz montaj ve balans hizmetinde.`,
        keywords: ['pirelli lastik', 'pirelli cinturato', 'pirelli scorpion', 'pirelli p zero', 'pirelli fiyatları']
    },
    'hankook': {
        name: 'Hankook',
        slug: 'hankook-lastik',
        title: 'Hankook Lastik Modelleri | Kore Teknolojisi',
        description: 'Hankook lastik çeşitleri Sakarya\'da. Ventus, Kinergy, Winter i*cept. ✓ Uygun Fiyat ✓ Kaliteli Ürün',
        content: `Hankook, Güney Kore'nin lider lastik markasıdır. Modern teknoloji ve uygun fiyat dengesi ile dünya çapında tercih edilmektedir.

**Hankook Teknoloji:**
- Kontrol teknolojisi ile yol tutuş
- Aqua jet kanalları ile su tahliyesi
- Low Rolling Resistance yakıt tasarrufu
- Uzun ömürlü karışım formülü

**Hankook Ürün Gamı:**
- **Ventus:** Performans ve spor
- **Kinergy:** Ekonomik ve çevreci
- **Winter i*cept:** Kış güvenliği
- **Dynapro:** SUV ve pikap

Sakarya'da 500+ Hankook lastik çeşidi stoklarımızda. Aynı gün teslimat imkanı.`,
        keywords: ['hankook lastik', 'hankook ventus', 'hankook kinergy', 'hankook fiyatları']
    },
    'petlas': {
        name: 'Petlas',
        slug: 'petlas-lastik',
        title: 'Petlas Lastik Çeşitleri | Yerli Üretim Kalitesi',
        description: 'Petlas lastik modelleri Sakarya\'da. Türk malı kaliteli lastikler. Snowmaster, Imperium, Explero. ✓ Uygun Fiyat',
        content: `Petlas, Türkiye'nin köklü lastik markalarından biridir. 1976'dan beri yerli üretim ile kaliteli ve ekonomik lastikler sunmaktadır.

**Petlas Avantajları:**
- Türk mühendislik tasarımı
- Avrupa standartlarında üretim
- Geniş ürün yelpazesi
- Uygun fiyat politikası

**Petlas Kategoriler:**
- **Snowmaster:** Kış lastiği
- **Imperium:** Yaz performans
- **Explero:** SUV lastiği
- **Multi Action:** 4 mevsim

Petlas'ın 500+ modelini Sakarya'da bulabilirsiniz. Yerli malı destekleyin, kaliteden ödün vermeyin.`,
        keywords: ['petlas lastik', 'petlas snowmaster', 'petlas imperium', 'petlas fiyatları', 'türk lastiği']
    },
    'kumho': {
        name: 'Kumho',
        slug: 'kumho-lastik',
        title: 'Kumho Lastik Modelleri | Kore Üretimi',
        description: 'Kumho lastik çeşitleri Sakarya\'da. Ecsta, Solus, Crugen serileri. ✓ Ekonomik ✓ Dayanıklı',
        content: `Kumho, Güney Kore'nin deneyimli lastik üreticisidir. 1960'tan beri kaliteli ve ekonomik lastikler üreten Kumho, dünya çapında tercih edilmektedir.

**Kumho Özellikleri:**
- ESCOT teknolojisi ile güvenlik
- 3D kerf dizaynı ile çekiş
- Silika karışım ile düşük direnç
- Motorsport test edilmiş

**Kumho Modelleri:**
- **Ecsta:** Performans lastiği
- **Solus:** Konfor serisi
- **Crugen:** SUV lastiği
- **WinterCraft:** Kış lastiği

500+ Kumho lastik çeşidi stoklarımızda. Sakarya'nın en uygun Kumho fiyatları bizde.`,
        keywords: ['kumho lastik', 'kumho ecsta', 'kumho solus', 'kumho fiyatları']
    },
    'goodyear': {
        name: 'Goodyear',
        slug: 'goodyear-lastik',
        title: 'Goodyear Lastik Modelleri | Amerikan Markası',
        description: 'Goodyear lastik çeşitleri Sakarya\'da. Eagle, EfficientGrip, UltraGrip. ✓ 125 Yıllık Tecrübe ✓ Kalite',
        content: `Goodyear, 1898'den beri Amerikan otomotiv endüstrisinin öncüsüdür. Ay'a ilk ayak basan astronotların lastiklerini Goodyear üretmiştir.

**Goodyear İnovasyon:**
- RunOnFlat teknolojisi
- FuelSaving yakıt tasarrufu
- SoundComfort sessiz sürüş
- NASA ile geliştirilen teknoloji

**Goodyear Serileri:**
- **Eagle:** Performans lastiği
- **EfficientGrip:** Ekonomik sürüş
- **UltraGrip:** Kış güvenliği
- **Wrangler:** Off-road

Goodyear'ın 125 yıllık deneyimini Sakarya'da yaşayın. Orijinal ürün garantisi ile.`,
        keywords: ['goodyear lastik', 'goodyear eagle', 'goodyear efficientgrip', 'goodyear ultragrip']
    },
    'yokohama': {
        name: 'Yokohama',
        slug: 'yokohama-lastik',
        title: 'Yokohama Lastik Çeşitleri | Japon Mühendisliği',
        description: 'Yokohama lastik modelleri Sakarya\'da. Advan, BluEarth, Geolandar. ✓ Japon Kalitesi ✓ Performans',
        content: `Yokohama, 1917'den beri Japon hassasiyeti ile üstün lastikler üretmektedir. Motorsport ve günlük kullanım için ideal çözümler sunar.

**Yokohama Teknolojiler:**
- Orange Oil teknolojisi
- BluEarth çevre dostu
- Nano Blend karışım
- Motorsport DNA

**Yokohama Ailesinde:**
- **Advan:** Sport performans
- **BluEarth:** Ekonomi ve çevre
- **Geolandar:** SUV ve arazi
- **iceGUARD:** Kış güvenliği

Sakarya'da Yokohama yetkili satıcısı. Profesyonel montaj ve garanti dahil.`,
        keywords: ['yokohama lastik', 'yokohama advan', 'yokohama blueearth', 'yokohama geolandar']
    },
    'falken': {
        name: 'Falken',
        slug: 'falken-lastik',
        title: 'Falken Lastik Modelleri | Performans Lastiği',
        description: 'Falken lastik çeşitleri Sakarya\'da. Azenis, Ziex, Eurowinter. ✓ Sport Lastik ✓ Uygun Fiyat',
        content: `Falken, Sumitomo Rubber Industries'in performans odaklı markasıdır. Motorsport kökleri ile bilinen Falken, yüksek performans sunar.

**Falken Özellikler:**
- 4D Nano Design teknolojisi
- Adaptive Constant Pressure
- Silent Core teknolojisi
- Drift ve yarış testli

**Falken Ürünler:**
- **Azenis:** Ultra performans
- **Ziex:** Günlük performans
- **Eurowinter:** Kış lastiği
- **Wildpeak:** Off-road

Falken'in gücünü Sakarya'da hissedin. Performans lastiklerinde uzman ekibimizle hizmetinizdeyiz.`,
        keywords: ['falken lastik', 'falken azenis', 'falken ziex', 'falken wildpeak']
    },
    'dunlop': {
        name: 'Dunlop',
        slug: 'dunlop-lastik',
        title: 'Dunlop Lastik Modelleri | İngiliz Kalitesi',
        description: 'Dunlop lastik çeşitleri Sakarya\'da. Sport Maxx, SP, Grandtrek. ✓ 130+ Yıllık Marka ✓ Güvenilir',
        content: `Dunlop, 1888'de John Boyd Dunlop tarafından kurulan tarihi lastik markasıdır. Motorsport ve günlük kullanımda lider konumdadır.

**Dunlop Miras:**
- 130+ yıllık deneyim
- Motorsport başarıları
- Multi-Radius teknolojisi
- Touch Technology yol hissi

**Dunlop Koleksiyon:**
- **Sport Maxx:** Yüksek performans
- **SP Sport:** Dengeli performans
- **Grandtrek:** SUV lastiği
- **Winter Sport:** Kış güvenliği

Dunlop'un köklü geçmişini Sakarya'da yaşayın. Kaliteli lastik, uzman montaj.`,
        keywords: ['dunlop lastik', 'dunlop sport maxx', 'dunlop grandtrek', 'dunlop fiyatları']
    },
    'nokian': {
        name: 'Nokian Tyres',
        slug: 'nokian-lastik',
        title: 'Nokian Tyres Lastik | Kuzey Ülkesi Teknolojisi',
        description: 'Nokian Tyres lastik modelleri Sakarya\'da. Kış lastiği uzmanı. Hakkapeliitta, WR. ✓ Finlandiya ✓ Kış Performansı',
        content: `Nokian Tyres, Finlandiya'nın önde gelen lastik üreticisidir. Özellikle kış lastiklerinde dünya lideridir. Kutup koşullarında test edilir.

**Nokian Uzmanlık:**
- Arctic Sense Grip teknolojisi
- Cryo Crystal 3 bileşeni
- Aramid fiber güçlendirme
- Kuzey kutbu testleri

**Nokian Ürün Gamı:**
- **Hakkapeliitta:** Premium kış
- **WR (Weather Proof):** 4 mevsim
- **Nordman:** Ekonomik kış
- **Powerproof:** Yaz lastiği

Kış güvenliğinde uzman Nokian Tyres, Sakarya'da stoklarımızda. Zorlu kış şartlarına hazır olun.`,
        keywords: ['nokian lastik', 'nokian hakkapeliitta', 'nokian wr', 'kış lastiği']
    },
    'starmaxx': {
        name: 'Starmaxx',
        slug: 'starmaxx-lastik',
        title: 'Starmaxx Lastik Çeşitleri | Ekonomik Lastik',
        description: 'Starmaxx lastik modelleri Sakarya\'da. Uygun fiyatlı kaliteli lastikler. ✓ Ekonomik ✓ Garantili',
        content: `Starmaxx, Petlas ailesinin ekonomik segmentteki markasıdır. Uygun fiyat ve kabul edilebilir kalite dengesi ile tercih edilir.

**Starmaxx Avantajları:**
- Ekonomik fiyat seviyesi
- Geniş ürün çeşitliliği
- Türkiye üretimi
- Garanti kapsamı

**Starmaxx Modeller:**
- **Incurro:** SUV lastiği
- **Ultrasport:** Performans
- **Novaro:** Konfor lastiği
- **Polarmaxx:** Kış lastiği

300+ Starmaxx model stoklarımızda. Ekonomik çözüm arayanlar için ideal. Sakarya'da en uygun fiyat.`,
        keywords: ['starmaxx lastik', 'starmaxx incurro', 'ekonomik lastik', 'ucuz lastik sakarya']
    },
    'sava': {
        name: 'Sava',
        slug: 'sava-lastik',
        title: 'Sava Lastik Modelleri | Goodyear Ailesi',
        description: 'Sava lastik çeşitleri Sakarya\'da. Goodyear teknolojisi ile ekonomik lastikler. ✓ Uygun Fiyat ✓ Kalite',
        content: `Sava, Goodyear'ın ekonomik segment markasıdır. Goodyear teknolojisi ve kalite standartları ile daha uygun fiyata lastik sunar.

**Sava Farkı:**
- Goodyear teknolojisi
- Avrupa üretimi
- Ekonomik fiyatlandırma
- Geniş model yelpazesi

**Sava Serileri:**
- **Intensa:** Yaz lastiği
- **Eskimo:** Kış güvenliği
- **All Weather:** 4 mevsim
- **Adapto:** SUV lastiği

Goodyear kalitesini ekonomik fiyata almak isteyenler için Sava ideal. Sakarya'da 200+ Sava model.`,
        keywords: ['sava lastik', 'sava intensa', 'sava eskimo', 'ekonomik goodyear']
    }
};

// Tüm etiketler listesi
const allTags = Object.keys(brandData).map(key => ({
    slug: brandData[key].slug,
    name: brandData[key].name
}));

const BrandTag = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalProducts, setTotalProducts] = useState(0);

    // Slug'dan marka bilgisini bul
    const brandKey = Object.keys(brandData).find(key => brandData[key].slug === slug);
    const brand = brandKey ? brandData[brandKey] : null;

    useEffect(() => {
        if (!brand) {
            navigate('/404');
            return;
        }

        // Markanın ürünlerini getir
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`, {
                    params: {
                        brand: brand.name,
                        page: 1,
                        limit: 12
                    }
                });
                setProducts(response.data.products || []);
                setTotalProducts(response.data.totalCount || 0);
            } catch (error) {
                console.error('Ürünler yüklenirken hata:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [brand, navigate]);

    if (!brand) {
        return null;
    }

    // Schema.org yapılandırılmış veri
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": brand.title,
        "description": brand.description,
        "url": `https://www.sakaryalastik.com/${brand.slug}`,
        "about": {
            "@type": "Product",
            "name": `${brand.name} Lastik`,
            "brand": {
                "@type": "Brand",
                "name": brand.name
            }
        },
        "provider": {
            "@type": "LocalBusiness",
            "name": "Sakarya Lastik",
            "areaServed": "Sakarya",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Adapazarı",
                "addressRegion": "Sakarya",
                "addressCountry": "TR"
            }
        },
        "keywords": brand.keywords.join(", ")
    };

    return (
        <>
            <Helmet>
                <title>{brand.title}</title>
                <meta name="description" content={brand.description} />
                <meta name="keywords" content={brand.keywords.join(', ')} />

                {/* Open Graph */}
                <meta property="og:title" content={brand.title} />
                <meta property="og:description" content={brand.description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://www.sakaryalastik.com/${brand.slug}`} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={brand.title} />
                <meta name="twitter:description" content={brand.description} />

                {/* Canonical */}
                <link rel="canonical" href={`https://www.sakaryalastik.com/${brand.slug}`} />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <Breadcrumb className="mb-6">
                    <Breadcrumb.Item>
                        <Link to="/">
                            <HomeOutlined /> Ana Sayfa
                        </Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <TagOutlined /> Etiketler
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{brand.name} Lastik</Breadcrumb.Item>
                </Breadcrumb>

                {/* Başlık ve Açıklama */}
                <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
                    <Title level={1} className="!mb-4">
                        <TagOutlined className="mr-3 text-orange-500" />
                        {brand.name} Lastik
                    </Title>

                    <div className="mb-6">
                        <Tag color="orange" className="text-base py-1 px-3">
                            {totalProducts} Ürün
                        </Tag>
                    </div>

                    <div className="prose max-w-none">
                        {brand.content.split('\n\n').map((paragraph, index) => {
                            // Başlıkları ayır
                            if (paragraph.startsWith('**') && paragraph.endsWith(':**')) {
                                return (
                                    <Title key={index} level={3} className="!mt-6 !mb-3">
                                        {paragraph.replace(/\*\*/g, '').replace(':', '')}
                                    </Title>
                                );
                            }
                            // Liste elemanları
                            else if (paragraph.startsWith('- **')) {
                                return (
                                    <ul key={index} className="list-disc pl-6 mb-4">
                                        {paragraph.split('\n').map((item, i) => (
                                            <li key={i} className="mb-2">
                                                <strong>{item.match(/\*\*(.*?)\*\*/)?.[1]}:</strong>
                                                {item.split('**')[2]?.replace(':', '')}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            }
                            // Normal paragraf
                            else {
                                return (
                                    <Paragraph key={index} className="text-gray-700 text-base leading-relaxed">
                                        {paragraph}
                                    </Paragraph>
                                );
                            }
                        })}
                    </div>
                </div>

                {/* Ürünler */}
                <div className="mb-8">
                    <Title level={2} className="mb-6">
                        {brand.name} Lastik Modelleri ({totalProducts})
                    </Title>

                    {loading ? (
                        <div className="text-center py-20">
                            <Spin size="large" />
                        </div>
                    ) : products.length > 0 ? (
                        <>
                            <Row gutter={[16, 16]}>
                                {products.map(product => (
                                    <Col key={product._id} xs={24} sm={12} md={8} lg={6}>
                                        <Card
                                            hoverable
                                            onClick={() => navigate(`/products/${product._id}`)}
                                            cover={
                                                <div className="h-48 bg-gray-100 flex items-center justify-center">
                                                    {product.downloaded_images && product.downloaded_images[0] ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_API_URL}/uploads/${product.downloaded_images[0]}`}
                                                            alt={product.name}
                                                            className="w-full h-full object-contain"
                                                        />
                                                    ) : (
                                                        <span className="text-gray-400">Resim Yok</span>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <Card.Meta
                                                title={<span className="text-sm line-clamp-2">{product.name}</span>}
                                                description={
                                                    <div className="mt-2">
                                                        <div className="text-orange-600 font-bold text-lg">
                                                            {product.price ? `${product.price.toLocaleString('tr-TR')} ₺` : 'Fiyat Belirtilmemiş'}
                                                        </div>
                                                        {product.tire_size && (
                                                            <div className="text-gray-500 text-xs mt-1">{product.tire_size}</div>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            <div className="text-center mt-8">
                                <Link
                                    to={`/lastik?brand=${brand.name}`}
                                    className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
                                >
                                    Tüm {brand.name} Lastikleri Gör ({totalProducts})
                                </Link>
                            </div>
                        </>
                    ) : (
                        <Empty description={`${brand.name} lastik bulunamadı`} />
                    )}
                </div>

                {/* Etiketler (Tags) */}
                <div className="bg-gray-50 rounded-lg p-6">
                    <Title level={3} className="!mb-4">
                        <TagOutlined /> Diğer Lastik Markaları
                    </Title>
                    <div className="flex flex-wrap gap-2">
                        {allTags
                            .filter(tag => tag.slug !== slug) // Mevcut etiket hariç
                            .map(tag => (
                                <Link
                                    key={tag.slug}
                                    to={`/${tag.slug}`}
                                    className="inline-block bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-500 px-4 py-2 rounded-full text-gray-700 hover:text-orange-600 transition-all"
                                >
                                    {tag.name} Lastik
                                </Link>
                            ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BrandTag;
